// Plan (epic) read-model data layer (#511). The two reads behind `/fleet/plans`
// and `/fleet/plans/:plan_id` — the index and the one-plan projection, the same
// pair `GET /v1/plans` and the MCP `plan` verb serve. Both are `createLiveQuery`
// (#7612), keyed on the session + active account (lib/digests' shape): a change on
// the `plans` topic re-READS them. That topic holds the 100 newest-activity plans
// and its row moves when a plan's tasks do (`plansTopic`, apps/api
// `stream-entity-topics.ts`). A plan BEHIND that head is never patched — 12 of 68
// active plans on the largest account (2026-10-30) — so every read holds the plans
// it shows BY ID ({@link planListLive}): the stream vouches for the read, and its
// 30 s poll stops, only while each of them is in the store. One outside it keeps
// the read on the primitive's poll, the backstop.
//
// STARTING a plan lives in `lib/plan-start.ts`, not here — the split
// `lib/tasks.ts` / `lib/task-dispatch.ts` already draws, and for the same reason:
// what a read model projects and what a dispatch answers change for different
// reasons. A start is `POST /v1/plans` (the same `PlanStarter` the flat
// `plan_start` MCP verb holds) and it answers a TASK id whose `run_id` stays null
// until a box claims the session, so nothing on this page can be re-read to
// confirm it: the plan appears here only once the scout emits `plan_authored`.

import {
  PLAN_LIST_MAX_PER_PAGE,
  type PlanBucket,
  type PlanListPage,
  type PlanProjection,
} from '@developerz/domain';
import type { CreateQueryResult } from '@tanstack/solid-query';
import { createSignal } from 'solid-js';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import type { LiveInterest } from './live/entities';
import { createLiveQuery, type LiveQueryOptions } from './live/live-query';
import { SESSION_TOKEN } from './session';

export const PLAN_QUERY_KEY = 'plan';
export const PLANS_QUERY_KEY = 'plans';
/** Distinct from {@link PLANS_QUERY_KEY} — a different server-side filter, not a client-side view of the same rows. */
export const CUSTOM_PLANS_QUERY_KEY = 'custom_plans';

/**
 * How many index rows to ask for — the ceiling that ACTUALLY exists, which is
 * the PORT's (`listPlansByAccount`, `@developerz/db`: default 50, hard
 * {@link PLAN_LIST_MAX_PER_PAGE}, 100 today) and
 * NOT the 1..200 `?per_page=` window `GET /v1/plans` and the `plan list` verb
 * both advertise. The port clamps silently, so asking for 200 returns at most
 * 100 rows — asking for the advertised ceiling instead of this one would make
 * every page past the first one short by construction, with nothing in the
 * envelope to say so.
 *
 * (#5136) Now genuinely the window: the `per_page` value this client always
 * sends, the value the port's clamp echoes back as `per_page` in the envelope,
 * and the value the page slice is taken against. The dashboard wiring to read
 * the envelope's `total`/`counts`/`repos` is THIS file's new hook shape
 * (`usePlans`/`useCustomPlans` return the WHOLE `PlanListPage`, not `.plans`).
 */
export const PLAN_PAGE_MAX = PLAN_LIST_MAX_PER_PAGE;

/**
 * The filter set the index list carries (#5136) — `bucket` / `repo` / `q` in
 * addition to the page, all of which the server honours as part of its SELECT.
 * `bucket` omitted (rather than `undefined`/null) for the "All" tab so the URL
 * stays clean; `q` omitted when empty for the same reason (an empty substring
 * is not a filter — same posture the server's own `PlanListQSchema` takes).
 */
export type PlanListParams = {
  readonly page: number;
  readonly bucket?: PlanBucket;
  readonly repo?: string;
  readonly q?: string;
};

/**
 * One plan's epic projection (groups in plan order). Idle until `planId()` is
 * set — the route gates on the `:plan_id` param. A missing/foreign plan 404s
 * at the API (the query returns null outside the tenant fence); the route
 * renders that as its not-found state, not the generic error.
 */
export function usePlan(planId: () => string | null, token: string | null = SESSION_TOKEN) {
  return createLiveQuery<PlanProjection>(() => {
    const id = planId();
    return {
      queryKey: [PLAN_QUERY_KEY, id, token, activeAccountId()],
      enabled: id !== null && token !== null,
      // The open plan's breakdown moves while you look (dispatch, merge, hold):
      // its `plans` row moves with it. Held by id, so a plan behind the topic's
      // head keeps the primitive's 30 s poll (the file header).
      live: id === null ? [] : [{ entity: 'plan', id }],
      queryFn: () => apiFetch<PlanProjection>(`/v1/plans/${id}`, token),
    };
  }, token).query;
}

/**
 * What a plan LIST holds: any plan (a new one arriving, or one moving into the
 * filter) plus every plan it shows, by id — so the stream vouches for the list
 * only while it holds each of them (the file header's backstop).
 */
export function planListLive(
  page: { readonly plans: readonly { readonly planId: string }[] } | undefined,
): LiveInterest[] {
  return [
    { entity: 'plan' },
    // The ids carry no matching (the type-wide interest hears every plan); they are
    // for LIVENESS — a plan past the `plans` topic's 100-row head keeps the page polling.
    ...(page?.plans ?? []).map((p): LiveInterest => ({ entity: 'plan', id: p.planId })),
  ];
}

/**
 * A plan list read live on {@link planListLive} of its OWN answer. The answer is
 * handed back through a signal set once, after the read exists, so the interests
 * track every new answer without an effect syncing state.
 */
function createLivePlanList(
  options: () => Omit<LiveQueryOptions<PlanListPage>, 'live'>,
  token: string | null,
): CreateQueryResult<PlanListPage> {
  const [read, setRead] = createSignal<CreateQueryResult<PlanListPage>>();
  const live = createLiveQuery<PlanListPage>(
    () => ({ ...options(), live: planListLive(read()?.data) }),
    token,
  ).query;
  setRead(() => live);
  return live;
}

/**
 * What stopping a whole epic did (#1906 + #2696), as `POST /v1/plans/:planId/cancel`
 * answers it.
 *
 * THREE lists for tasks, not a count, and `skipped_review_task_ids` is the
 * load-bearing one: a `pr_review` slice cannot be stopped, and an operator
 * who pressed stop is owed the list of what kept running rather than a
 * quieter success. Always 200 when the plan exists — a plan whose slices
 * all finished is a plan with nothing to stop, not a failed request.
 *
 * (#2696) `cancelled_group_count` carries the number of plan groups this
 * call flipped to `cancelled` — held groups on a fully-held plan AND the
 * held half of a partially-held plan. A fully-dispatched plan stamps 0
 * (the dispatched half's tasks are cancelled THROUGH their `tasks` rows,
 * not through the group's own flip). The dashboard renders it distinctly
 * from `cancelled_task_ids` so an operator stops hearing "Nothing to stop,
 * every slice had already finished" when they cancelled a held plan — the
 * same lie this issue exists to kill.
 */
export type PlanCancelResult = {
  readonly plan_id: string;
  /**
   * The plan's POST-CAS status (#4037): `done` here means the plan was already
   * landed before this call — `planStopMessage` renders `already landed`
   * distinctly from the `nothing to stop` lie a `cancelled` re-cancel prints.
   * Absent on responses predating #4037; treat as the message's existing
   * wording (every block — cancelled/already/skipped — is still reported).
   */
  readonly plan_status?: 'open' | 'cancelled' | 'done' | 'ended';
  readonly cancelled_task_ids: readonly string[];
  readonly cancelled_group_count?: number;
  readonly skipped_review_task_ids: readonly string[];
  readonly already_terminal_task_ids: readonly string[];
  readonly skipped_reason?: string;
};

/** Stop every unfinished slice of one epic. 404 ⇒ unknown or another account's plan. */
export function cancelPlan(
  planId: string,
  token: string | null = SESSION_TOKEN,
): Promise<PlanCancelResult> {
  return apiFetch<PlanCancelResult>(`/v1/plans/${encodeURIComponent(planId)}/cancel`, token, {
    method: 'POST',
  });
}

/**
 * The account's plans, newest-activity-first — the #5135 envelope in full
 * (`PlanListPage`: `plans` + `page` + `per_page` + `total` + `counts` +
 * `repos`), NOT just the row array. Numbered offset paging (#5136), `page=1`
 * is real and so is every page past it; the dashboard renders its tab
 * counts from `counts`, the pager from `page`/`total`, and the repo filter
 * from `repos` — none of which a bare `.plans` payload carries.
 *
 * STYLE follows `lib/artifacts.ts`'s `useArtifacts`/`listQuery` pair for the
 * keyed query string; the hooks diverge in their RETURN shape (whole envelope,
 * not just rows), because `counts`/`total`/`repos` are part of the answer a
 * tabs+pager UI needs.
 */
export function usePlans(
  params: () => { page: number },
  token: string | null = SESSION_TOKEN,
): CreateQueryResult<PlanListPage> {
  return createLivePlanList(
    () => ({
      queryKey: [PLANS_QUERY_KEY, params().page, token, activeAccountId()],
      enabled: token !== null,
      // Keep the previous page's rows visible while the next page loads — the
      // same `placeholderData: (prev) => prev` `useMe` uses for the org switch
      // (lib/session.ts:~181). Without it the pager flash from "page 1 rows" to
      // a loading state every click.
      placeholderData: (prev: PlanListPage | undefined) => prev,
      queryFn: () => apiFetch<PlanListPage>(`/v1/plans${planListQuery(params())}`, token),
    }),
    token,
  );
}

/** The reactive key one `useCustomPlans` fetch is filed under. Shared by the
 * query itself and its scoped `placeholderData` below so "same filter" is
 * defined in exactly one place. */
function customPlansQueryKey(params: PlanListParams, token: string | null): readonly unknown[] {
  return [
    CUSTOM_PLANS_QUERY_KEY,
    params.page,
    params.bucket ?? '',
    params.repo ?? '',
    params.q ?? '',
    token,
    activeAccountId(),
  ];
}

/**
 * The account's plans a PERSON started (#4340's `?initiated_by=user`,
 * `user` = a dashboard session or a credential bound to a person, #4727;
 * `system` = no person is recorded as its initiator, so a system-initiated
 * plan is NOT in this list), newest-activity-first
 * — the Custom plans section's own list, kept as a SEPARATE query from
 * {@link usePlans} rather than a client-side filter over it: the "all plans"
 * panel's newest-100 window can hold as few as 1 of an account's 23 custom
 * plans (measured 2026-09-17, the largest account), because a plan matching
 * this filter can easily fall outside the newest-100-of-everything head.
 * Filtering in SQL, before that cap is taken, is the only way to see every
 * one.
 *
 * (#5136) Returns the WHOLE `PlanListPage`, the same envelope {@link usePlans}
 * does. The Custom plans section's tabs read `counts`; the row list reads
 * `plans`; the pager reads `total`; the bucket filter goes through `params`
 * so narrowing re-reads server-side and never filters rows the client
 * happens to hold.
 */
export function useCustomPlans(
  params: () => PlanListParams,
  token: string | null = SESSION_TOKEN,
): CreateQueryResult<PlanListPage> {
  return createLivePlanList(() => {
    const key = customPlansQueryKey(params(), token);
    return {
      queryKey: key,
      enabled: token !== null,
      // Keep the previous rows ONLY across a page change or a live re-read —
      // i.e. only when bucket/repo/q/token/account are UNCHANGED from the
      // request `previousQuery` answers. An unconditional `(prev) => prev`
      // would paint the OLD filter's rows under a NEWLY selected tab/repo/
      // search — "here are your Stalled plans" while still showing
      // In-progress ones. `previousQuery.queryKey` is the key AT that earlier
      // fetch; comparing every slot but `page`
      // (index 1) against THIS render's key is "same filter, next page" vs.
      // "a different filter" without re-deriving what "same filter" means a
      // second time. A genuine filter change now shows the loading skeleton
      // for a beat instead — see `CustomPlansPanels.tsx`'s header comment.
      placeholderData: (prev: PlanListPage | undefined, previousQuery) => {
        if (prev === undefined || previousQuery === undefined) return undefined;
        const prevKey = previousQuery.queryKey;
        const sameFilter = prevKey.every((v, i) => i === 1 || v === key[i]);
        return sameFilter ? prev : undefined;
      },
      queryFn: () => apiFetch<PlanListPage>(`/v1/plans${planListQuery(params(), 'user')}`, token),
    };
  }, token);
}

/**
 * The `?…` query string for `GET /v1/plans` — always `per_page=100` (the port
 * clamps, and `PLAN_PAGE_MAX` is the clamped ceiling), always `page` (incl.
 * `page=1` — absent page is not a request, it is a stale URL). `bucket` is
 * OMITTED (not set to an empty string) for the All tab so the URL stays
 * clean. `q` is OMITTED when empty/missing — an empty substring is not a
 * filter, and the server's own `PlanListQSchema` (`min(1)`) rejects it. A
 * `bucket`/`repo`/`q` filter passes through the SAME value the server's own
 * schema validates; this client never re-decides what counts.
 *
 * `initiated_by=user` is added ONLY when explicitly passed, so `usePlans`
 * (the all-plans panel) sends no `initiated_by` and `useCustomPlans` sends
 * `user` — matching the route's own "omitted, never defaulted" stance on an
 * absent filter.
 */
export function planListQuery(params: PlanListParams, initiatedBy?: 'user'): string {
  const search = new URLSearchParams();
  search.set('per_page', String(PLAN_PAGE_MAX));
  search.set('page', String(params.page));
  if (params.bucket !== undefined) search.set('bucket', params.bucket);
  if (params.repo !== undefined && params.repo.length > 0) search.set('repo', params.repo);
  if (params.q !== undefined && params.q.length > 0) search.set('q', params.q);
  if (initiatedBy !== undefined) search.set('initiated_by', initiatedBy);
  return `?${search.toString()}`;
}
